import React, { useState } from 'react';
import './ProjectionsChart.css';
import APP_CONFIG from '../config/appConfig';
import { TIER_SERIES_COLOR } from '../lib/pricing';

/**
 * Projection charts — three small multiples rather than one combined plot.
 *
 * Revenue and platform cost differ by an order of magnitude, so drawing them
 * together flattens every cost line onto the baseline. A second y-axis would
 * "fix" that and is never correct, so each measure gets its own chart at its
 * own scale instead. Tier colours are the validated categorical set from
 * pricing.js, keyed to the tier and never to its rank.
 */

const PAD = { top: 22, right: 22, bottom: 38, left: 62 };
const W = 720;
const H = 250;
const TICKS = 4;

/** Compact money for ticks and labels: $1.2M, $68K, $940. */
const compact = (value, symbol = '$') => {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1000000) return `${sign}${symbol}${(abs / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
    if (abs >= 1000) return `${sign}${symbol}${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
    return `${sign}${symbol}${Math.round(abs)}`;
};

/**
 * A step from the 1/2/2.5/5 family so every gridline lands on a round number
 * and the axis maximum is an exact multiple of the step.
 */
const niceScale = (rawMax, rawMin = 0, count = TICKS) => {
    const span = (rawMax - rawMin) || 1;
    const rough = span / count;
    const mag = Math.pow(10, Math.floor(Math.log10(Math.abs(rough) || 1)));
    const norm = rough / mag;
    const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
    const min = rawMin < 0 ? Math.floor(rawMin / step) * step : 0;
    const max = min + step * Math.ceil((rawMax - min) / step || 1);
    return { min, max, step };
};

/** Bar with only its data-end rounded; the baseline end stays square. */
const barPath = (x, baseY, valueY, width, r = 4) => {
    const up = valueY <= baseY;
    const h = Math.abs(baseY - valueY);
    const radius = Math.min(r, width / 2, h);
    if (h < 0.5) return `M ${x} ${baseY} h ${width}`;
    return up
        ? `M ${x} ${baseY} L ${x} ${valueY + radius} Q ${x} ${valueY} ${x + radius} ${valueY}
           L ${x + width - radius} ${valueY} Q ${x + width} ${valueY} ${x + width} ${valueY + radius}
           L ${x + width} ${baseY} Z`
        : `M ${x} ${baseY} L ${x} ${valueY - radius} Q ${x} ${valueY} ${x + radius} ${valueY}
           L ${x + width - radius} ${valueY} Q ${x + width} ${valueY} ${x + width} ${valueY - radius}
           L ${x + width} ${baseY} Z`;
};

const ProjectionsChart = ({ projections, currencySymbol = '$', monthlyGrowthRate }) => {
    const [view, setView] = useState('chart');
    const [hover, setHover] = useState(null);

    const packages = APP_CONFIG.packages;
    const { quarterly, annual, pricedTiers } = projections;
    const seriesLabel = (t) => packages[t].name;

    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const xLine = (i) => PAD.left + (quarterly.length === 1 ? plotW / 2 : (i * plotW) / (quarterly.length - 1));

    const scaleFor = (values) => {
        const s = niceScale(Math.max(0, ...values), Math.min(0, ...values));
        const span = s.max - s.min || 1;
        return {
            ...s,
            y: (v) => PAD.top + plotH - ((v - s.min) / span) * plotH,
            ticks: Array.from({ length: TICKS + 1 }, (_, i) => s.min + s.step * i)
        };
    };

    // Four points per series - cheap enough to derive on every render.
    const revenueScale = scaleFor(quarterly.map(q => q.revenue));
    const profitScale = scaleFor(quarterly.flatMap(q => pricedTiers.map(t => q.tiers[t].profit)));

    // Quarter totals are three months of recurring cost, so a third of the
    // non-first quarter is the clean monthly figure (the first quarter can
    // carry legacy one-time fees).
    const steadyQuarter = quarterly[quarterly.length - 1];
    const monthlyByTier = {};
    pricedTiers.forEach(t => { monthlyByTier[t] = steadyQuarter.tiers[t].totalCost / 3; });

    const groupW = plotW / quarterly.length;
    // Capped at 24px: the leftover band is deliberate air, not padding.
    const barW = Math.min(24, Math.max(6, (groupW * 0.56) / pricedTiers.length - 2));

    /** Keep the tooltip inside the plot at the extremes. */
    const tooltipAnchor = (index, centre) => {
        const pct = (centre / W) * 100;
        const edge = index === 0 ? 'start' : index === quarterly.length - 1 ? 'end' : 'mid';
        return { left: `${pct}%`, className: `chart-tooltip anchor-${edge}` };
    };

    const Axis = ({ scale, showZero }) => (
        <>
            {scale.ticks.map((tick, i) => (
                <g key={i}>
                    <line x1={PAD.left} x2={W - PAD.right} y1={scale.y(tick)} y2={scale.y(tick)} className="chart-grid" />
                    <text x={PAD.left - 10} y={scale.y(tick) + 4} className="chart-tick" textAnchor="end">
                        {compact(tick, currencySymbol)}
                    </text>
                </g>
            ))}
            <line
                x1={PAD.left} x2={W - PAD.right}
                y1={scale.y(showZero ? 0 : scale.min)} y2={scale.y(showZero ? 0 : scale.min)}
                className="chart-axis"
            />
        </>
    );

    const XLabels = ({ mode }) => (
        <>
            {quarterly.map((q, i) => (
                <text
                    key={i}
                    x={mode === 'bar' ? PAD.left + i * groupW + groupW / 2 : xLine(i)}
                    y={H - PAD.bottom + 20}
                    className="chart-axis-label"
                    textAnchor="middle"
                >
                    {q.quarterShort}
                </text>
            ))}
        </>
    );

    const HitAreas = ({ chart, mode }) => (
        <>
            {quarterly.map((q, i) => (
                <rect
                    key={i}
                    x={mode === 'bar' ? PAD.left + i * groupW : xLine(i) - plotW / (quarterly.length * 2)}
                    y={PAD.top}
                    width={mode === 'bar' ? groupW : plotW / quarterly.length}
                    height={plotH}
                    fill="transparent"
                    onMouseEnter={() => setHover({ chart, index: i })}
                />
            ))}
        </>
    );

    return (
        <div className="projections-chart">
            <div className="chart-toolbar">
                <div className="chart-heading">
                    <h3>Projection charts</h3>
                    <p>Compounding at {monthlyGrowthRate}% per month. Each quarter is a three-month total.</p>
                </div>
                <div className="chart-view-toggle" role="tablist" aria-label="Projection view">
                    <button role="tab" aria-selected={view === 'chart'}
                        className={view === 'chart' ? 'active' : ''} onClick={() => setView('chart')}>
                        Charts
                    </button>
                    <button role="tab" aria-selected={view === 'table'}
                        className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>
                        Table
                    </button>
                </div>
            </div>

            {view === 'table' ? (
                <div className="chart-table-wrap">
                    <table className="chart-table">
                        <caption>Quarterly revenue, cost and profit by tier</caption>
                        <thead>
                            <tr>
                                <th scope="col">Quarter</th>
                                <th scope="col">Revenue</th>
                                {pricedTiers.map(t => <th key={t + 'c'} scope="col">{seriesLabel(t)} cost</th>)}
                                {pricedTiers.map(t => <th key={t + 'p'} scope="col">{seriesLabel(t)} profit</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {quarterly.map(q => (
                                <tr key={q.quarter}>
                                    <th scope="row">{q.quarter}</th>
                                    <td>{compact(q.revenue, currencySymbol)}</td>
                                    {pricedTiers.map(t => <td key={t + 'c'}>{compact(q.tiers[t].totalCost, currencySymbol)}</td>)}
                                    {pricedTiers.map(t => (
                                        <td key={t + 'p'} className={q.tiers[t].profit < 0 ? 'neg' : ''}>
                                            {compact(q.tiers[t].profit, currencySymbol)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            <tr className="chart-table-total">
                                <th scope="row">Annual</th>
                                <td>{compact(annual.revenue, currencySymbol)}</td>
                                {pricedTiers.map(t => <td key={t + 'c'}>{compact(annual.tiers[t].totalCost, currencySymbol)}</td>)}
                                {pricedTiers.map(t => (
                                    <td key={t + 'p'} className={annual.tiers[t].profit < 0 ? 'neg' : ''}>
                                        {compact(annual.tiers[t].profit, currencySymbol)}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                <>
                    {/* ---------- 1. Revenue: one series, so the title names it ---------- */}
                    <figure className="chart-figure">
                        <figcaption>Revenue growth</figcaption>
                        <div className="chart-svg-wrap">
                            <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" role="img"
                                aria-label="Area chart of projected quarterly revenue"
                                onMouseLeave={() => setHover(null)}>
                                <Axis scale={revenueScale} showZero />
                                <path
                                    d={`M ${xLine(0)} ${revenueScale.y(revenueScale.min)} `
                                        + quarterly.map((q, i) => `L ${xLine(i)} ${revenueScale.y(q.revenue)}`).join(' ')
                                        + ` L ${xLine(quarterly.length - 1)} ${revenueScale.y(revenueScale.min)} Z`}
                                    className="chart-revenue-area"
                                />
                                <path
                                    d={quarterly.map((q, i) => `${i === 0 ? 'M' : 'L'} ${xLine(i)} ${revenueScale.y(q.revenue)}`).join(' ')}
                                    className="chart-line" stroke="var(--brand-primary-dark)"
                                />
                                {quarterly.map((q, i) => (
                                    <circle key={i} cx={xLine(i)} cy={revenueScale.y(q.revenue)} r="4.5"
                                        fill="var(--brand-primary-dark)" className="chart-marker" />
                                ))}
                                {/* Direct labels on the endpoints only, never every point. */}
                                {[0, quarterly.length - 1].map(i => (
                                    <text key={'dl' + i} x={xLine(i)} y={revenueScale.y(quarterly[i].revenue) - 12}
                                        className="chart-direct-label"
                                        textAnchor={i === 0 ? 'start' : 'end'}>
                                        {compact(quarterly[i].revenue, currencySymbol)}
                                    </text>
                                ))}
                                {hover?.chart === 'rev' && (
                                    <line x1={xLine(hover.index)} x2={xLine(hover.index)}
                                        y1={PAD.top} y2={H - PAD.bottom} className="chart-crosshair" />
                                )}
                                <HitAreas chart="rev" mode="line" />
                                <XLabels mode="line" />
                            </svg>
                            {hover?.chart === 'rev' && (
                                <div {...tooltipAnchor(hover.index, xLine(hover.index))}>
                                    <strong>{quarterly[hover.index].quarter}</strong>
                                    <span className="tt-row">
                                        Revenue<b>{compact(quarterly[hover.index].revenue, currencySymbol)}</b>
                                    </span>
                                </div>
                            )}
                        </div>
                    </figure>

                    {/* ---------- 2. Monthly cost by tier ----------
                        A bar comparison, not a line over quarters: cost is flat
                        across the projection, so a time axis would plot a
                        constant and collapse the three tiers onto one another.
                        Zero baseline, so the bar lengths are honest. */}
                    <figure className="chart-figure">
                        <figcaption>Monthly cost by tier</figcaption>
                        <div className="chart-svg-wrap">
                            <svg
                                viewBox={`0 0 ${W} ${28 + pricedTiers.length * 46}`}
                                className="chart-svg"
                                role="img"
                                aria-label="Bar chart comparing the monthly cost of each tier"
                                onMouseLeave={() => setHover(null)}
                            >
                                {(() => {
                                    const rowH = 46;
                                    const labelW = 104;
                                    const trackW = W - labelW - 86;
                                    const maxCost = Math.max(...pricedTiers.map(t => monthlyByTier[t])) || 1;
                                    const scale = niceScale(maxCost);
                                    return pricedTiers.map((t, i) => {
                                        const value = monthlyByTier[t];
                                        const w = Math.max(2, (value / scale.max) * trackW);
                                        const y = 14 + i * rowH;
                                        const hot = hover?.chart === 'tiercost' && hover.tier === t;
                                        return (
                                            <g key={t} onMouseEnter={() => setHover({ chart: 'tiercost', tier: t })}>
                                                <text x={labelW - 12} y={y + 20} className="chart-axis-label" textAnchor="end">
                                                    {seriesLabel(t)}
                                                </text>
                                                <rect x={labelW} y={y + 4} width={trackW} height={24} rx="4" className="chart-track" />
                                                <path
                                                    d={`M ${labelW} ${y + 28} L ${labelW} ${y + 8}
                                                        Q ${labelW} ${y + 4} ${labelW + 4} ${y + 4}
                                                        L ${labelW + w - 4} ${y + 4}
                                                        Q ${labelW + w} ${y + 4} ${labelW + w} ${y + 8}
                                                        L ${labelW + w} ${y + 28} Z`}
                                                    fill={TIER_SERIES_COLOR[t]}
                                                    className={`chart-bar ${hot ? 'hot' : ''}`}
                                                />
                                                <text x={labelW + w + 10} y={y + 21} className="chart-direct-label">
                                                    {compact(value, currencySymbol)}
                                                </text>
                                            </g>
                                        );
                                    });
                                })()}
                            </svg>
                        </div>
                        <p className="chart-footnote">
                            Cost is flat across the projection — only revenue grows — so this compares the
                            tiers at their current monthly price rather than over time.
                        </p>
                    </figure>

                    {/* ---------- 3. Profit by tier ---------- */}
                    <figure className="chart-figure">
                        <figcaption>Quarterly profit by tier</figcaption>
                        <div className="chart-legend">
                            {pricedTiers.map(t => (
                                <span className="legend-item" key={t}>
                                    <span className="legend-swatch" style={{ background: TIER_SERIES_COLOR[t] }} />
                                    {seriesLabel(t)}
                                </span>
                            ))}
                        </div>
                        <div className="chart-svg-wrap">
                            <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" role="img"
                                aria-label="Grouped bar chart of projected quarterly profit for each tier"
                                onMouseLeave={() => setHover(null)}>
                                <Axis scale={profitScale} />
                                {quarterly.map((q, qi) => {
                                    const groupX = PAD.left + qi * groupW;
                                    const inset = (groupW - (barW + 2) * pricedTiers.length) / 2;
                                    return (
                                        <g key={qi}>
                                            {pricedTiers.map((t, ti) => {
                                                const value = q.tiers[t].profit;
                                                const hot = hover?.chart === 'profit' && hover.index === qi;
                                                return (
                                                    <path
                                                        key={t}
                                                        d={barPath(groupX + inset + ti * (barW + 2), profitScale.y(0), profitScale.y(value), barW)}
                                                        fill={TIER_SERIES_COLOR[t]}
                                                        className={`chart-bar ${hot ? 'hot' : ''}`}
                                                    />
                                                );
                                            })}
                                        </g>
                                    );
                                })}
                                {/* zero baseline drawn last so it reads above the fills */}
                                <line x1={PAD.left} x2={W - PAD.right} y1={profitScale.y(0)} y2={profitScale.y(0)} className="chart-axis" />
                                <HitAreas chart="profit" mode="bar" />
                                <XLabels mode="bar" />
                            </svg>
                            {hover?.chart === 'profit' && (
                                <div {...tooltipAnchor(hover.index, PAD.left + hover.index * groupW + groupW / 2)}>
                                    <strong>{quarterly[hover.index].quarter}</strong>
                                    {pricedTiers.map(t => (
                                        <span className="tt-row" key={t}>
                                            <span className="tt-swatch" style={{ background: TIER_SERIES_COLOR[t] }} />
                                            {seriesLabel(t)}
                                            <b className={quarterly[hover.index].tiers[t].profit < 0 ? 'neg' : ''}>
                                                {compact(quarterly[hover.index].tiers[t].profit, currencySymbol)}
                                            </b>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </figure>
                </>
            )}
        </div>
    );
};

export default ProjectionsChart;
